import { Grid, Hidden } from '@material-ui/core';
import { Form, Formik, FormikProps } from 'formik';
import React, { useContext, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormWrapper } from '../../../../global/components/form-wrapper';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { MainContent } from '../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { useApiResponseError } from '../../../../global/hooks/useApiError';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { useGpsType } from '../../../../global/hooks/useGpsType';
import { DemographicAppKeys, DemographicsContext } from '../../../../global/models/demographics-model';
import { TcsmStates } from '../../../../root/tcsm-model';
import { PerformPersonSearchContext, PersonSearchContext, PersonSearchState } from '../../../arc/features/person-search/person-search-model';
import { PersonSearchResults } from '../../../arc/features/person-search/person-search-results';
import { PersonSearchToastCombo } from '../../../arc/features/person-search/person-search-toast-combo';
import { AccountInfoRoutes } from '../../constants/routes';
import { FormTypes, matchedPersonField } from '../../models/form-models';
import { PointOfContactDepentType, PointOfContactFormModel } from '../mycpg-midyear/models/mycpg-mid-year-models';
import { DocumentUploadState } from '../personal-info/document-upload-models';
import { stageDocuments } from '../personal-info/document-upload-slice';
import PointOfContact from '../point-of-contact/point-of-contact';
import { DomesticPartnerActionFields } from './components/domestic-partner-action';
import { DomesticPartnerAgreements } from './components/domestic-partner-agreements';
import { DomesticPartnerConfirmation } from './components/domestic-partner-confirmation';
import { DomesticPartnerDeathFields } from './components/domestic-partner-death-fields';
import { DomesticPartnerDissolutionFields } from './components/domestic-partner-dissolution-fields';
import { DomesticPartnerEstablishmentFields } from './components/domestic-partner-establishment-form-fields';
import { DomesticPartnerFormAction, DomesticPartnerFormModel } from './model/domestic-partner-form-models';
import { DomesticPartnerWizard } from './wizard/domestic-partner-wizard';
import { useQuery } from '../../../../global/hooks/useQuery';


interface Props {
    basePath: string;
    wizard: DomesticPartnerWizard;
}

interface DomesticPartnerState {
    values: DomesticPartnerFormModel;
    formikHelpers: FormikProps<DomesticPartnerFormModel>;
    documentUploadState: DocumentUploadState;
}

export function DomesticPartnerForm(props: Props) {
    const { basePath, wizard } = props;

    const dispatch = useDispatch();
    const app = useContext(DemographicsContext);
    const personSearchKey = useContext(PersonSearchContext);
    const personSearchState = useSelector<TcsmStates, PersonSearchState>(state => state.personSearchState[personSearchKey]);
    const apiErrors = useApiResponseError();
    const initialValues = wizard?.getInitialFormValue() ?? {};
    const gpsItem = useGpsType(wizard?.getGpsType());
    const personGuid = useSelector<TcsmStates, string>(state => state.personalInfo[app]?.currentPerson?.person_guid);
    const documentUploadState = useSelector<TcsmStates, DocumentUploadState>(state => state.documentUpload[app]);
    const myCpgMidYearPath = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${AccountInfoRoutes.Midyear}${AccountInfoRoutes.PersonalInfoSegment}`);
    const mapRelationshipPath = `${basePath}${AccountInfoRoutes.RelationshipsSegment}`;
    const searchResultsRef = useRef<HTMLDivElement>(null);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showPointOfContact, setShowPointOfContact] = useState(false);
    const [currentDomesticPartnerState, setCurrentDomesticPartnerState] = useState<DomesticPartnerState>(null);
    const dependentType: PointOfContactDepentType = PointOfContactDepentType.DomesticPartner;
    const plusSixtyFive = true;

    const sucessFormType: FormTypes[] = useQuery()?.get('success')?.split(',')?.map(a => a as FormTypes);    


    function OnSubmitHandler(
        values: DomesticPartnerFormModel,
        formikHelpers: FormikProps<DomesticPartnerFormModel>,
        pointOfContactFormValues?: PointOfContactFormModel) {

        setCurrentDomesticPartnerState({ values, formikHelpers, documentUploadState });

        wizard.onSubmit(
            values,
            formikHelpers,
            personGuid,
            documentUploadState,
            myCpgMidYearPath,
            mapRelationshipPath,
            mapRelationshipPath,
            personSearchState,
            selectedPerson,
            setSelectedPerson,
            setShowConfirmation,
            setShowPointOfContact,
            pointOfContactFormValues,
        );
    }

    function OnPointOfContactSubmit(pointOfContactFormValues: PointOfContactFormModel) {
        if (currentDomesticPartnerState?.formikHelpers &&
            currentDomesticPartnerState?.values) {

            wizard.onSubmit(
                currentDomesticPartnerState?.values,
                currentDomesticPartnerState?.formikHelpers,
                personGuid,
                currentDomesticPartnerState?.documentUploadState,
                myCpgMidYearPath,
                mapRelationshipPath,
                mapRelationshipPath,
                personSearchState,
                selectedPerson,
                setSelectedPerson,
                setShowConfirmation,
                setShowPointOfContact,
                pointOfContactFormValues,
            );
        }
    }

    function OnPointOfContactCancel() {
        setShowPointOfContact(false);

        const loadedFiles = currentDomesticPartnerState?.documentUploadState?.stagedDocuments;
        // If there are files loaded, stage them again
        // The document state is reset when the contact form is displayed.
        if (loadedFiles && loadedFiles.length > 0) {
            dispatch(stageDocuments(loadedFiles, app));
        }
    }

    function RenderFormContent(formProps: FormikProps<DomesticPartnerFormModel>) {

        if (showConfirmation) {
            return (
                <DomesticPartnerConfirmation
                    labelSetting={wizard?.getLabelSettings()}
                    relationshipsProps={wizard?.getRelationShipSettings()}
                    formProps={formProps}
                    selectedPerson={selectedPerson}
                />);
        }


        switch (formProps.values?.formAction) {
            case DomesticPartnerFormAction.ESTABLISH:
                return (
                    <PerformPersonSearchContext.Provider value={wizard.shouldRenderOsccrSearch(formProps.values, showConfirmation)}>
                        <DomesticPartnerEstablishmentFields
                            formProps={formProps}
                            wizard={wizard}
                            plusSixtyFive={plusSixtyFive}
                        />
                    </PerformPersonSearchContext.Provider >
                );
            case DomesticPartnerFormAction.DISSOLUTION:
                return (
                    <DomesticPartnerDissolutionFields
                        formProps={formProps}
                        wizard={wizard}
                    />);
            case DomesticPartnerFormAction.DEATH:
                return (
                    <DomesticPartnerDeathFields
                        formProps={formProps}
                        wizard={wizard}
                    />);
            default:
                return null;
        }
    }

    return (
        <PageContainer>
            <MainContent >                
                { /* Hide the form but keep it rendering to keep the state */}
                <div className={showPointOfContact ? 'hide' : ''}>
                    <Formik
                        initialValues={initialValues}
                        onSubmit={OnSubmitHandler}
                        enableReinitialize
                        initialErrors={apiErrors}
                        validationSchema={wizard?.getValidationSchema(showConfirmation)}
                    >
                        {(formProps: FormikProps<DomesticPartnerFormModel>) => (
                            <>
                                <Form>
                                    <Grid>
                                        <FormWrapper
                                            formikProps={formProps}
                                            submitText={wizard?.getSubmitButtonText()}
                                            cancelText={wizard?.getCancelButtonText()}
                                            onCancel={() => wizard?.onCancel()}
                                            submitDisabled={wizard?.shouldDisableSubmit(formProps?.values)}
                                            successfulForms={sucessFormType}
                                            formType={FormTypes.ESTABLISH_DOMESTIC_PARTNER}
                                            defaultShowDocUpload={wizard?.shouldShowDefaultDocumentUpload(formProps?.values)}
                                            formKeys={Object.keys(initialValues)}
                                            additionalContentAboveButtons={<DomesticPartnerAgreements formProps={formProps} wizard={wizard} />}
                                            personSearchToast={
                                                <PersonSearchToastCombo
                                                    searchResultsRef={searchResultsRef}
                                                    renderResultsMessaging={wizard?.shouldRenderOsccrSearch(formProps?.values, showConfirmation) && personSearchState.searchResults?.length > 0 && !selectedPerson}
                                                />
                                            }
                                            personSearchResults={
                                                wizard?.shouldRenderOsccrSearch(formProps?.values, showConfirmation) &&
                                                <PersonSearchResults
                                                    groupName={matchedPersonField}
                                                    backToTopAction={wizard?.onNavigateToTop}
                                                    results={personSearchState?.searchResults}
                                                    resultsRef={searchResultsRef} />
                                            }
                                        >
                                            <PageHeader
                                                headerText={wizard?.getPageHeader()}
                                                description={wizard.getPageDescription()}
                                            />
                                            <Hidden mdUp>
                                                <SideBarContent>
                                                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                                                </SideBarContent>
                                            </Hidden>
                                            <DividerSection>
                                                <DomesticPartnerActionFields
                                                    formProps={formProps}
                                                    disabled={false}
                                                    wizard={wizard}
                                                />
                                            </DividerSection>
                                            {
                                                RenderFormContent(formProps)
                                            }
                                        </FormWrapper>
                                    </Grid>
                                </Form>
                            </>
                        )}
                    </Formik>
                </div>

                {showPointOfContact && <PointOfContact isMyCpg={app === DemographicAppKeys.myCPG} onSubmitHandler={OnPointOfContactSubmit} onCancelHandler={OnPointOfContactCancel} dependentType={dependentType} />}

            </MainContent>
            <Hidden smDown>
                <SideBarContent>
                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                </SideBarContent>
            </Hidden>

        </PageContainer >

    );
}
